import { useCallback, useEffect, useMemo, useState } from 'react'
import { listComputationRuns } from '../../../api/calc'
import { listProgramFilingAdjustments } from '../../../api/cases'
import { listCreditInstrumentAdjustments, getCreditInstrumentByFiling } from '../../../api/credit'
import { computeReadinessScore, getReadinessScore } from '../../../api/evidence'
import { listEvents } from '../../../api/events'
import { listSpendLineAdjustments, listSpendLines } from '../../../api/film'
import { ApiError } from '../../../api/problem'
import { label } from '../../../lib/labels'
import { useProgramRefs } from '../../../lib/useProgramRefs'
import type { CaseView } from '../../../types/case'
import type { ProgramFilingDetailView } from '../../../types/programFilingDetail'
import type {
  CaseAnalyticsHeader,
  FilingOption,
  SectionState,
} from './CaseAnalyticsPanel'
import type { DonutSlice } from '../../insights/charts/DonutChart'
import type { WaterfallStep } from '../../insights/charts/WaterfallChart'
import type { AdjustmentHistoryRowView } from './AdjustmentsHistoryTable'
import type { FilingStatusStep } from './FilingStatusTimeline'
import {
  buildComputationWaterfall,
  buildFilingStatusTimeline,
  buildReadinessGaps,
  buildSpendBreakdown,
  mergeAdjustmentHistory,
  type RawAdjustmentSource,
} from './analyticsMappers'

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? (err.problem.detail ?? err.message) : fallback
}

function formatAmount(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
  } catch {
    return `${currency} ${amount.toFixed(2)}`
  }
}

export type CaseAnalyticsTab = 'spend' | 'evidence' | 'timeline'

export interface UseCaseAnalyticsResult {
  header: CaseAnalyticsHeader
  filings: FilingOption[]
  selectedFilingId: string | null
  onSelectFiling: (filingId: string) => void

  spend: SectionState<{ slices: DonutSlice[]; totalDisplay: string }>
  onSpendSliceClick: (slice: DonutSlice) => void
  onViewSpendTab: () => void
  onRetrySpend: () => void

  computation: SectionState<{ steps: WaterfallStep[] }>
  onRetryComputation: () => void

  readiness: SectionState<{ percent: number | null; gaps: { id: string; label: string }[] }>
  onComputeReadinessNow: () => void
  onGapClick: (gapId: string) => void
  onRetryReadiness: () => void

  timeline: SectionState<{ steps: FilingStatusStep[]; additionalEventCount: number }>
  onViewFullTimeline: () => void
  onRetryTimeline: () => void

  adjustments: SectionState<{ rows: AdjustmentHistoryRowView[] }>
  onRetryAdjustments: () => void
}

/**
 * The `/cases/{id}/analytics` tab's data-wiring pass (IB-282 View 2) — composes the real endpoints
 * the design doc/DoR name (`ComputationRunController`, `ReadinessScoreController`, the universal
 * events Timeline API, `QualifiedSpendLineApi`, `AdjustmentHistoryPort` via the three per-target
 * controllers) and feeds `CaseAnalyticsPanel`, mirroring `useWidgetData`'s per-tile independence
 * (IB-162 posture) — one section's fetch failure never blocks another section's load.
 *
 * `onNavigateTab` lets the panel's "View spend detail"/gap-click/"View full timeline" drill actions
 * switch this same `CaseDetailScreen`'s tab state (an in-shell tab switch, never a route navigation —
 * design doc §4's IA-budget table, RAID R-UX282-2).
 */
export function useCaseAnalytics(
  caseView: CaseView,
  filings: ProgramFilingDetailView[],
  onNavigateTab: (tab: CaseAnalyticsTab) => void,
): UseCaseAnalyticsResult {
  const [selectedFilingId, setSelectedFilingId] = useState<string | null>(filings[0]?.id ?? null)
  useEffect(() => {
    if (filings.length > 0 && !filings.some((f) => f.id === selectedFilingId)) {
      setSelectedFilingId(filings[0]?.id ?? null)
    }
  }, [filings, selectedFilingId])

  const programRefs = useProgramRefs(filings.map((f) => f.programId))
  const filingOptions: FilingOption[] = useMemo(
    () =>
      filings.map((f) => ({
        id: f.id,
        label: programRefs.get(f.programId)?.display ?? `Program ${f.programId.slice(0, 8)}`,
      })),
    [filings, programRefs],
  )
  const selectedFiling = filings.find((f) => f.id === selectedFilingId) ?? null

  const [spend, setSpend] = useState<SectionState<{ slices: DonutSlice[]; totalDisplay: string }>>({ status: 'loading' })
  const [computation, setComputation] = useState<SectionState<{ steps: WaterfallStep[] }>>({ status: 'loading' })
  const [readiness, setReadiness] = useState<
    SectionState<{ percent: number | null; gaps: { id: string; label: string }[] }>
  >({ status: 'loading' })
  const [timeline, setTimeline] = useState<
    SectionState<{ steps: FilingStatusStep[]; additionalEventCount: number }>
  >({ status: 'loading' })
  const [adjustments, setAdjustments] = useState<SectionState<{ rows: AdjustmentHistoryRowView[] }>>({
    status: 'loading',
  })

  // Cross-tile inputs the KPI header composes from (no second fetch — reused verbatim from the
  // sections' own loads below).
  const [creditComputedDisplay, setCreditComputedDisplay] = useState<string | null>(null)
  const [readinessPercent, setReadinessPercent] = useState<number | null>(null)
  const [qreTotalDisplay, setQreTotalDisplay] = useState<string | null>(null)

  // ---------------------------------------------------------------------
  // 2.1 — QRE / qualified-spend breakdown (film only — RAID R-UX282-1)
  // ---------------------------------------------------------------------
  const loadSpend = useCallback(() => {
    if (caseView.kind !== 'film') {
      setSpend({
        status: 'coming_soon',
        message: 'R&D spend breakdown ships once module.case_’s Project/ProjectResource read port exists.',
      })
      setQreTotalDisplay(null)
      return
    }
    if (!caseView.productionId) {
      setSpend({ status: 'empty', message: 'No production linked to this case yet.' })
      setQreTotalDisplay(null)
      return
    }
    setSpend((s) => ({ ...s, status: 'loading' }))
    listSpendLines(caseView.productionId)
      .then((rows) => {
        const breakdown = buildSpendBreakdown(rows)
        setSpend(breakdown ? { status: 'ok', data: breakdown } : { status: 'empty' })
        setQreTotalDisplay(breakdown?.totalDisplay ?? null)
      })
      .catch((err) => setSpend({ status: 'error', message: errorMessage(err, 'Could not load qualified spend.') }))
  }, [caseView.kind, caseView.productionId])

  // ---------------------------------------------------------------------
  // 2.2 — Credit computation breakdown (pinned FINAL ComputationRun)
  // ---------------------------------------------------------------------
  const loadComputation = useCallback(() => {
    if (!selectedFiling) {
      // No filing exists to select (a Case with zero ProgramFilings) -- a real, if unusual, state:
      // 'ok' with zero steps, exactly like "no FINAL run yet" (the panel's own children render the
      // "not yet computed" message either way, never a fabricated distinction between the two).
      setComputation({ status: 'ok', data: { steps: [] } })
      return
    }
    setComputation((s) => ({ ...s, status: 'loading' }))
    listComputationRuns(selectedFiling.id)
      .then((page) => {
        const run = page.items.find((r) => r.kind === 'final') ?? null
        setComputation({ status: 'ok', data: { steps: buildComputationWaterfall(run) } })
      })
      .catch((err) => setComputation({ status: 'error', message: errorMessage(err, 'Could not load the computation run.') }))
  }, [selectedFiling])

  // Credit-instrument read backs both the KPI header and the 2.5 adjustments merge — one fetch, two
  // consumers (mirrors `CreditPanel`'s own `getCreditInstrumentByFiling` reuse).
  const [creditInstrumentId, setCreditInstrumentId] = useState<string | null>(null)
  useEffect(() => {
    if (!selectedFiling) {
      setCreditInstrumentId(null)
      setCreditComputedDisplay(null)
      return
    }
    let cancelled = false
    getCreditInstrumentByFiling(selectedFiling.id)
      .then((instrument) => {
        if (cancelled) return
        setCreditInstrumentId(instrument?.id ?? null)
        setCreditComputedDisplay(instrument ? formatAmount(instrument.certifiedAmount, instrument.currency) : null)
      })
      .catch(() => {
        if (!cancelled) {
          setCreditInstrumentId(null)
          setCreditComputedDisplay(null)
        }
      })
    return () => {
      cancelled = true
    }
  }, [selectedFiling])

  // ---------------------------------------------------------------------
  // 2.3 — Evidence readiness (closes IB-146)
  // ---------------------------------------------------------------------
  const loadReadiness = useCallback(() => {
    if (!selectedFiling) {
      setReadiness({ status: 'ok', data: { percent: null, gaps: [] } })
      setReadinessPercent(null)
      return
    }
    setReadiness((s) => ({ ...s, status: 'loading' }))
    getReadinessScore(selectedFiling.id)
      .then((score) => {
        if (!score) {
          setReadiness({ status: 'ok', data: { percent: null, gaps: [] } })
          setReadinessPercent(null)
          return
        }
        const percent = Math.round(score.coverage)
        setReadiness({ status: 'ok', data: { percent, gaps: buildReadinessGaps(score.gaps) } })
        setReadinessPercent(percent)
      })
      .catch((err) => setReadiness({ status: 'error', message: errorMessage(err, 'Could not load the readiness score.') }))
  }, [selectedFiling])

  const onComputeReadinessNow = useCallback(() => {
    if (!selectedFiling) return
    setReadiness((s) => ({ ...s, status: 'loading' }))
    computeReadinessScore(selectedFiling.id)
      .then((score) => {
        const percent = Math.round(score.coverage)
        setReadiness({ status: 'ok', data: { percent, gaps: buildReadinessGaps(score.gaps) } })
        setReadinessPercent(percent)
      })
      .catch((err) => setReadiness({ status: 'error', message: errorMessage(err, 'Could not compute the readiness score.') }))
  }, [selectedFiling])

  // ---------------------------------------------------------------------
  // 2.4 — Filing-status timeline (real, hash-chained events, ADR-0046)
  // ---------------------------------------------------------------------
  const loadTimeline = useCallback(() => {
    if (!selectedFiling) {
      setTimeline({ status: 'empty', message: 'No filing selected.' })
      return
    }
    setTimeline((s) => ({ ...s, status: 'loading' }))
    listEvents({ targetType: 'ProgramFiling', targetId: selectedFiling.id, limit: 50 })
      .then((page) => setTimeline({ status: 'ok', data: buildFilingStatusTimeline(page.items) }))
      .catch((err) => setTimeline({ status: 'error', message: errorMessage(err, 'Could not load the filing timeline.') }))
  }, [selectedFiling])

  // ---------------------------------------------------------------------
  // 2.5 — Adjustments history (QSL + ProgramFiling + CreditInstrument, merged)
  // ---------------------------------------------------------------------
  const loadAdjustments = useCallback(() => {
    if (!selectedFiling) {
      setAdjustments({ status: 'empty', message: 'No filing selected.' })
      return
    }
    setAdjustments((s) => ({ ...s, status: 'loading' }))

    const filingId = selectedFiling.id
    const programFilingSourceP: Promise<RawAdjustmentSource[]> = listProgramFilingAdjustments(filingId)
      .then((rows) =>
        rows.map((r) => ({
          targetType: 'ProgramFiling',
          targetId: filingId,
          targetLabel: 'Program filing',
          fieldName: r.fieldName,
          priorValue: r.priorValue,
          newValue: r.newValue,
          currencyCode: r.currencyCode,
          method: r.method,
          reason: r.reason,
          actorId: r.actorId,
          channel: r.channel,
          appliedAt: r.appliedAt,
        })),
      )
      .catch(() => [] as RawAdjustmentSource[])

    const creditInstrumentSourceP: Promise<RawAdjustmentSource[]> = creditInstrumentId
      ? listCreditInstrumentAdjustments(creditInstrumentId)
          .then((rows) =>
            rows.map((r) => ({
              targetType: 'CreditInstrument',
              targetId: creditInstrumentId,
              targetLabel: 'Credit instrument',
              fieldName: r.fieldName,
              priorValue: r.priorValue,
              newValue: r.newValue,
              currencyCode: r.currencyCode,
              method: r.method,
              reason: r.reason,
              actorId: r.actorId,
              channel: r.channel,
              appliedAt: r.appliedAt,
            })),
          )
          .catch(() => [] as RawAdjustmentSource[])
      : Promise.resolve([])

    const spendSourceP: Promise<RawAdjustmentSource[]> =
      caseView.kind === 'film' && caseView.productionId
        ? listSpendLines(caseView.productionId)
            .then((rows) =>
              Promise.all(
                rows.map((row) =>
                  listSpendLineAdjustments(row.id)
                    .then((records) =>
                      records.map(
                        (r): RawAdjustmentSource => ({
                          targetType: 'QualifiedSpendLine',
                          targetId: row.id,
                          targetLabel: label('spendCategory', row.category),
                          fieldName: r.fieldName,
                          priorValue: r.priorValue,
                          newValue: r.newValue,
                          currencyCode: null,
                          method: r.method,
                          reason: r.reason,
                          actorId: r.actorId,
                          channel: r.channel,
                          appliedAt: r.appliedAt,
                        }),
                      ),
                    )
                    .catch(() => [] as RawAdjustmentSource[]),
                ),
              ),
            )
            .then((perLine) => perLine.flat())
            .catch(() => [] as RawAdjustmentSource[])
        : Promise.resolve([])

    Promise.all([programFilingSourceP, creditInstrumentSourceP, spendSourceP])
      .then(([programFilingRows, creditInstrumentRows, spendRows]) => {
        const merged = mergeAdjustmentHistory([...programFilingRows, ...creditInstrumentRows, ...spendRows])
        // 'ok' regardless of row count -- AdjustmentsHistoryTable renders its own "No adjustments
        // recorded on this case yet." paragraph for `rows: []` (same convention as timeline above),
        // never WidgetFrame's generic `empty` framing.
        setAdjustments({ status: 'ok', data: { rows: merged } })
      })
      .catch((err) => setAdjustments({ status: 'error', message: errorMessage(err, 'Could not load adjustment history.') }))
  }, [selectedFiling, creditInstrumentId, caseView.kind, caseView.productionId])

  useEffect(() => {
    loadSpend()
  }, [loadSpend])
  useEffect(() => {
    loadComputation()
  }, [loadComputation])
  useEffect(() => {
    loadReadiness()
  }, [loadReadiness])
  useEffect(() => {
    loadTimeline()
  }, [loadTimeline])
  useEffect(() => {
    loadAdjustments()
  }, [loadAdjustments])

  const header: CaseAnalyticsHeader = {
    creditComputedDisplay,
    qreTotalDisplay,
    readinessPercent,
    filingStatusLabel: selectedFiling ? label('programFilingState', selectedFiling.currentState) : null,
  }

  return {
    header,
    filings: filingOptions,
    selectedFilingId,
    onSelectFiling: setSelectedFilingId,

    spend,
    onSpendSliceClick: () => {
      // Cross-filter is handled entirely in-tile by CaseAnalyticsPanel's own local state (design
      // doc §3's "same tile" resolution, RAID R-UX282-2) — this hook has nothing further to do on
      // a slice click; the callback exists so the panel's contract stays symmetric with every other
      // section's drill/retry callback.
    },
    onViewSpendTab: () => onNavigateTab('spend'),
    onRetrySpend: loadSpend,

    // No real "Compute now" trigger exists anywhere in this app today (disclosed gap — see the
    // Developer hand-off notes): creating a NEW `ComputationRun` needs a `kind`/`asOf`/
    // `inputBindings` choice `POST /program-filings/{id}/computation-runs` requires, and no screen
    // in this codebase makes that choice for a user yet. Rather than fabricate a blind default
    // (CLAUDE.md's "never a dead/fabricated action"), this tile's empty state renders no CTA at all
    // — an honest "not yet computed", not a button that silently guesses (see CaseAnalyticsPanel's
    // own header comment for the full rationale).
    computation,
    onRetryComputation: loadComputation,

    readiness,
    onComputeReadinessNow,
    onGapClick: () => onNavigateTab('evidence'),
    onRetryReadiness: loadReadiness,

    timeline,
    onViewFullTimeline: () => onNavigateTab('timeline'),
    onRetryTimeline: loadTimeline,

    adjustments,
    onRetryAdjustments: loadAdjustments,
  }
}
